import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPaperPlane,
    faComments,
    faInfoCircle,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';

const SendTicketTemplateModal = ({
    show,
    onHide,
    ticket,
    token,
    currentUserName,
    onConfirm
}) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const [templates, setTemplates] = useState([]);
    const [selectedKey, setSelectedKey] = useState('BIENVENIDA');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!show) return;

        setSelectedKey('BIENVENIDA');
        const fetchTemplates = async () => {
            setIsLoading(true);
            try {
                const response = await fetch('/api/SupportEngine/GetFacebookTemplates', {
                    headers: {
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + token
                    }
                });

                if (response.ok) {
                    const result = await response.json();
                    const list = result.data || [];
                    setTemplates(list);
                    if (list.length > 0) {
                        setSelectedKey(list[0].vClaveMensaje);
                    }
                }
            } catch {
                setTemplates([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTemplates();
    }, [show, token]);

    if (!ticket) return null;

    const ticketId = ticket.idTicket || ticket.id;
    const clientName = ticket.vCustomerName?.trim() || ticket.vTelefono || 'Cliente';
    const agentDisplayName = currentUserName || ticket.vNombreUsuarioAsignado || 'Asesor';

    const handleConfirmSend = async () => {
        if (!selectedKey) return;
        setIsSubmitting(true);
        try {
            const selectedTpl = templates.find(t => t.vClaveMensaje === selectedKey);
            const previewText = (selectedTpl?.vMensajePersonalizado || selectedTpl?.vPrefijo || '')
                .replace(/{{nombre_agente}}|{{nombre_asesor}}/g, agentDisplayName);
            await onConfirm(ticket, selectedKey, selectedTpl?.vNombrePlantilla, previewText);
            if (onHide) onHide();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            contentClassName={`border-0 shadow-lg overflow-hidden ${isDark ? 'dark__bg-1000 text-light' : 'bg-white text-dark'}`}
        >
            <Modal.Header
                closeButton
                closeVariant={isDark ? 'white' : undefined}
                className="py-3 px-3 d-flex align-items-center justify-content-between"
                style={{
                    backgroundColor: isDark ? '#16222f' : '#f8fafc',
                    borderBottom: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
                }}
            >
                <div className="d-flex align-items-center gap-2">
                    <div
                        className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                            width: '36px',
                            height: '36px',
                            backgroundColor: '#4f46e5',
                            color: '#ffffff',
                            fontSize: '1rem'
                        }}
                    >
                        <FontAwesomeIcon icon={faPaperPlane} />
                    </div>
                    <div>
                        <h6 className="mb-0 fw-bold" style={{ fontSize: '0.96rem' }}>
                            Enviar Plantilla de Facebook
                        </h6>
                        <small style={{ fontSize: '0.78rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                            Ticket #{ticketId} &bull; {clientName}
                        </small>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="p-3">
                {/* Banner Informativo sobre política de 24h de Meta */}
                <div
                    className="p-2.5 rounded-3 border mb-3 d-flex align-items-start gap-2"
                    style={{
                        backgroundColor: isDark ? 'rgba(79, 70, 229, 0.18)' : '#eef2ff',
                        borderColor: isDark ? 'rgba(99, 102, 241, 0.4)' : '#c7d2fe',
                        color: isDark ? '#e0e7ff' : '#312e81',
                        fontSize: '0.82rem',
                        lineHeight: '1.45'
                    }}
                >
                    <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="mt-1 flex-shrink-0"
                        style={{ color: isDark ? '#a5b4fc' : '#4f46e5' }}
                    />
                    <div>
                        Han pasado <strong>más de 24 horas</strong> desde la última interacción. Para continuar la atención según las políticas de Facebook, selecciona una plantilla para reanudar el chat. <em>Esta acción no alterará la asignación ni los tiempos de respuesta.</em>
                    </div>
                </div>

                <div className="mb-2">
                    <label
                        className="form-label fw-semibold mb-2"
                        style={{
                            fontSize: '0.84rem',
                            color: isDark ? '#f1f5f9' : '#1e293b'
                        }}
                    >
                        Selecciona la plantilla a enviar:
                    </label>

                    {isLoading ? (
                        <div className="text-center py-4" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span style={{ fontSize: '0.84rem' }}>Cargando plantillas...</span>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-2">
                            {templates.map(tpl => {
                                const isSelected = selectedKey === tpl.vClaveMensaje;
                                const title = tpl.vClaveMensaje === 'BIENVENIDA'
                                    ? 'Plantilla de Asignación / Saludo Inicial'
                                    : (tpl.vClaveMensaje || 'Plantilla de Reenganche');
                                const previewText = (tpl.vMensajePersonalizado || tpl.vPrefijo || 'Hola. Gracias por comunicarte con nuestro equipo...')
                                    .replace(/{{nombre_agente}}|{{nombre_asesor}}/g, agentDisplayName);

                                return (
                                    <div
                                        key={tpl.idMensaje || tpl.idCatMensajesAutomaticos || tpl.vClaveMensaje}
                                        onClick={() => setSelectedKey(tpl.vClaveMensaje)}
                                        className="p-3 rounded-3 border cursor-pointer transition-base"
                                        style={{
                                            backgroundColor: isSelected
                                                ? (isDark ? 'rgba(79, 70, 229, 0.22)' : '#eef2ff')
                                                : (isDark ? '#1a2736' : '#ffffff'),
                                            borderColor: isSelected
                                                ? (isDark ? '#818cf8' : '#6366f1')
                                                : (isDark ? '#334155' : '#cbd5e1'),
                                            boxShadow: isSelected
                                                ? (isDark ? '0 0 0 1px #818cf8' : '0 0 0 1px #6366f1')
                                                : 'none',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <FontAwesomeIcon
                                                    icon={isSelected ? faCheckCircle : faComments}
                                                    style={{
                                                        color: isSelected
                                                            ? (isDark ? '#a5b4fc' : '#4f46e5')
                                                            : (isDark ? '#64748b' : '#94a3b8'),
                                                        fontSize: '0.95rem'
                                                    }}
                                                />
                                                <span
                                                    className="fw-bold"
                                                    style={{
                                                        fontSize: '0.88rem',
                                                        color: isSelected
                                                            ? (isDark ? '#c7d2fe' : '#312e81')
                                                            : (isDark ? '#f8fafc' : '#0f172a')
                                                    }}
                                                >
                                                    {title}
                                                </span>
                                            </div>
                                            <span
                                                className="badge px-2 py-0.5 rounded-pill fw-semibold"
                                                style={{
                                                    fontSize: '0.68rem',
                                                    backgroundColor: isSelected
                                                        ? (isDark ? '#4f46e5' : '#6366f1')
                                                        : (isDark ? '#334155' : '#e2e8f0'),
                                                    color: isSelected
                                                        ? '#ffffff'
                                                        : (isDark ? '#cbd5e1' : '#475569')
                                                }}
                                            >
                                                Meta
                                            </span>
                                        </div>

                                        <p
                                            className="mb-0"
                                            style={{
                                                fontSize: '0.84rem',
                                                lineHeight: '1.5',
                                                whiteSpace: 'pre-wrap',
                                                color: isSelected
                                                    ? (isDark ? '#f1f5f9' : '#1e293b')
                                                    : (isDark ? '#cbd5e1' : '#334155')
                                            }}
                                        >
                                            {previewText}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer
                className="py-2.5 px-3 d-flex align-items-center justify-content-end gap-2"
                style={{
                    backgroundColor: isDark ? '#16222f' : '#f8fafc',
                    borderTop: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
                }}
            >
                <Button
                    variant="link"
                    size="sm"
                    disabled={isSubmitting}
                    onClick={onHide}
                    className="text-secondary text-decoration-none shadow-none"
                    style={{ fontSize: '0.84rem' }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    disabled={isSubmitting || isLoading || !selectedKey}
                    onClick={handleConfirmSend}
                    className="px-3 py-1.5 fw-semibold d-flex align-items-center gap-2 shadow-sm"
                    style={{
                        backgroundColor: '#4f46e5',
                        borderColor: '#4f46e5',
                        fontSize: '0.84rem',
                        borderRadius: '7px'
                    }}
                >
                    {isSubmitting ? (
                        <>
                            <Spinner animation="border" size="sm" />
                            <span>Enviando...</span>
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faPaperPlane} />
                            <span>Enviar Plantilla Facebook</span>
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

SendTicketTemplateModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    ticket: PropTypes.object,
    token: PropTypes.string,
    currentUserName: PropTypes.string,
    onConfirm: PropTypes.func.isRequired
};

export default SendTicketTemplateModal;
